import React, { useMemo } from 'react'
import { CircularProgress, useTheme } from '@material-ui/core'
import { AvatarCircle, Container } from '../../components'
import { Blanket, BlanketRect } from '../../components/Blanket'

import { getInitials } from '../../helpers'
import NumberFormat from 'react-number-format'
import { useAptConfig } from '../../hooks'
import { CustomStatus, ModelMap } from '../../models'
import { useSelector } from 'react-redux'
import { GET_INSPECTION_ANALYTICS_REQUEST, RootState } from '../../store'
import { useStyles } from '../../styles'
import { InvInsAnalyticsBlanketData } from './InspectionDetail'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    setInvConfAnalyticsBlanketData: (
        invConfAnalyticsBlanketData: InvInsAnalyticsBlanketData,
    ) => void
    setInvConfAnalyticsBlanketOpen: (open: boolean) => void
}

export interface StatusAnalytic {
    id: number
    status: CustomStatus
    cost: number
    total: number
}

export interface InvConfigAnalytic {
    name: string
    total: number
    cost: number
    statusDict: ModelMap<StatusAnalytic>
}

export const InspectionAnalytics = (props: Props) => {
    const {
        setInvConfAnalyticsBlanketData,
        setInvConfAnalyticsBlanketOpen,
    } = props
    const theme = useTheme()

    const classes = useStyles()

    // Memoize style object to keep stable reference for useMemo dependency
    const statusStyle: React.CSSProperties = useMemo(
        () => ({
            cursor: 'pointer',
            borderRadius: theme.shape.borderRadius,
        }),
        [theme.shape.borderRadius],
    )

    const { getInventoryConfigMap } = useAptConfig({})
    const inventoryConfigMap = getInventoryConfigMap()

    const inspectionAnalytics = useSelector(
        (state: RootState) => state.inspection.inspectionAnalytics,
    )

    const isLoading = useSelector(
        (state: RootState) =>
            state.inspection.isLoading[GET_INSPECTION_ANALYTICS_REQUEST],
    )

    // Memoize the expensive analytics computation to avoid recalculating on every render
    const inventoryConfigAnalyticComponents = useMemo(() => {
        if (!inspectionAnalytics) return []

        const components: JSX.Element[] = []

        Object.keys(inspectionAnalytics.inventory_config_analytics).forEach(
            (invConfIdStr) => {
                if (invConfIdStr === 'null') return

                const invConfId = Number(invConfIdStr)
                const invConfig = inventoryConfigMap[invConfId]
                let cost = 0
                let count = 0

                const statusAnalytics = Object.keys(
                    inspectionAnalytics.inventory_config_analytics[
                        invConfIdStr
                    ],
                ).map((statusId) => {
                    const statusAnalytic =
                        inspectionAnalytics.inventory_config_analytics[
                            invConfIdStr
                        ][statusId]
                    cost += statusAnalytic['cost'] ?? 0
                    count += statusAnalytic['count']

                    if (statusId === 'null') return null

                    return (
                        <Container
                            key={`STATUS_ANALYTIC_${invConfIdStr}_${statusId}`}
                            style={{
                                flexDirection: 'column',
                                marginTop: theme.spacing(1),
                                ...statusStyle,
                            }}
                            onClick={() => {
                                setInvConfAnalyticsBlanketData({
                                    status: statusAnalytic.name,
                                    inventoryConfig: invConfig,
                                    statusCount: statusAnalytic.count,
                                    sumPrice: statusAnalytic.cost ?? 0,
                                    statusId: statusId,
                                })
                                setInvConfAnalyticsBlanketOpen(true)
                            }}
                            className={classes.hoverGrey300}
                        >
                            <Container style={{ alignItems: 'center' }}>
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.subtitle1,
                                        ),
                                        fontWeight:
                                            theme.typography.fontWeightBold,
                                    }}
                                >
                                    {statusAnalytic.name}:
                                </span>
                                <NumberFormat
                                    thousandSeparator
                                    value={statusAnalytic.count}
                                    displayType="text"
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.subtitle1,
                                        ),
                                        fontWeight:
                                            theme.typography.fontWeightLight,
                                        marginLeft: theme.spacing(1),
                                    }}
                                />
                            </Container>
                            <NumberFormat
                                prefix="$"
                                thousandSeparator
                                value={statusAnalytic.cost}
                                displayType="text"
                                style={{
                                    ...typographyStyle(
                                        theme.typography.subtitle2,
                                    ),
                                    fontWeight:
                                        theme.typography.fontWeightMedium,
                                }}
                            />
                        </Container>
                    )
                })

                components.push(
                    <Container
                        key={`BLANKET_ANALYTIC_${invConfIdStr}`}
                        style={{
                            backgroundColor: theme.palette.grey[200],
                            borderRadius: theme.shape.borderRadius,
                            border: `1px solid ${theme.palette.grey[400]}`,
                            margin: theme.spacing(2),
                            padding: theme.spacing(2),
                            flexDirection: 'column',
                            flexShrink: 1,
                        }}
                    >
                        <Container style={{ alignItems: 'center' }}>
                            <Container
                                style={{
                                    flexDirection: 'column',
                                    marginRight: theme.spacing(2),
                                }}
                            >
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.subtitle1,
                                        ),
                                        fontWeight:
                                            theme.typography.fontWeightBold,
                                    }}
                                >
                                    {invConfig?.name}
                                </span>
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.subtitle2,
                                        ),
                                        fontWeight:
                                            theme.typography.fontWeightLight,
                                    }}
                                >
                                    {invConfig?.sku}
                                </span>
                            </Container>
                            <AvatarCircle
                                image={invConfig?.picture}
                                text={getInitials(invConfig?.name)}
                            />
                        </Container>

                        <Container
                            style={{
                                flexDirection: 'column',
                                marginTop: theme.spacing(2),
                            }}
                        >
                            <Container
                                style={{ alignItems: 'center', ...statusStyle }}
                                onClick={() => {
                                    setInvConfAnalyticsBlanketData({
                                        status: 'Total',
                                        inventoryConfig: invConfig,
                                        statusCount: count,
                                        sumPrice: cost,
                                        statusId: undefined,
                                    })
                                    setInvConfAnalyticsBlanketOpen(true)
                                }}
                                className={classes.hoverGrey300}
                            >
                                <span
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.subtitle1,
                                        ),
                                        fontWeight:
                                            theme.typography.fontWeightBold,
                                    }}
                                >
                                    Total:
                                </span>
                                <NumberFormat
                                    thousandSeparator
                                    value={count}
                                    displayType="text"
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.subtitle1,
                                        ),
                                        fontWeight:
                                            theme.typography.fontWeightLight,
                                        marginLeft: theme.spacing(1),
                                    }}
                                />
                            </Container>
                            <NumberFormat
                                prefix="$"
                                thousandSeparator
                                value={cost}
                                displayType="text"
                                style={{
                                    ...typographyStyle(
                                        theme.typography.subtitle2,
                                    ),
                                    fontWeight:
                                        theme.typography.fontWeightMedium,
                                }}
                            />
                            {statusAnalytics}
                        </Container>
                    </Container>,
                )
            },
        )

        return components
    }, [inspectionAnalytics, inventoryConfigMap])

    if (isLoading) {
        return (
            <Container
                style={{
                    backgroundColor: theme.palette.grey[100],
                    flex: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                }}
            >
                <CircularProgress size={48} />
            </Container>
        )
    }

    if (
        !inspectionAnalytics ||
        inventoryConfigAnalyticComponents.length === 0
    ) {
        return (
            <Container
                style={{
                    backgroundColor: theme.palette.grey[100],
                    flex: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    flexDirection: 'column',
                }}
            >
                <span
                    style={{
                        ...typographyStyle(theme.typography.h6),
                        color: theme.palette.text.secondary,
                        marginBottom: theme.spacing(1),
                    }}
                >
                    No Analytics Data
                </span>
                <span
                    style={{
                        ...typographyStyle(theme.typography.body2),
                        color: theme.palette.text.disabled,
                    }}
                >
                    Complete your inspections to see analytics here.
                </span>
            </Container>
        )
    }

    return (
        <Container
            style={{
                backgroundColor: theme.palette.grey[100],
                flex: 1,
                flexDirection: 'column',
            }}
            scrollY
        >
            <Container style={{ flexWrap: 'wrap' }}>
                {inventoryConfigAnalyticComponents}
            </Container>
        </Container>
    )
}
